import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Brain, Watch, Smartphone, Zap, TrendingUp, AlertTriangle, CheckCircle2, Scan, RefreshCw, Activity, Search, Send, MessageSquare, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { glucoseAPI, analyticsAPI, insightsAPI, foodAPI, aiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SmartFeatures = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Barcode Scanner State
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  
  // Food Search State
  const [foodSearch, setFoodSearch] = useState('');
  const [foodResults, setFoodResults] = useState([]);
  const [searchingFood, setSearchingFood] = useState(false);
  
  // AI Chat State
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [glucoseData, analyticsData, insightsData] = await Promise.all([
        glucoseAPI.getAll(30).catch(() => []),
        analyticsAPI.getSummary().catch(() => null),
        insightsAPI.getAll(5).catch(() => [])
      ]);

      setAnalytics(analyticsData);
      setPredictions(insightsData);

      // Simulated device data
      setDeviceData({
        device_name: 'Health Tracker Pro',
        last_sync: new Date().toISOString(),
        steps_today: Math.round(3000 + Math.random() * 7000),
        heart_rate_avg: Math.round(65 + Math.random() * 20),
        sleep_hours: (6 + Math.random() * 2).toFixed(1),
        active_minutes: Math.round(20 + Math.random() * 40)
      });

    } catch (error) {
      console.error('Error fetching smart features data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGeneratePrediction = async () => {
    setGenerating(true);
    try {
      const newInsight = await insightsAPI.generate();
      setPredictions([newInsight, ...predictions]);
      toast({
        title: "AI Insight Generated!",
        description: "New personalized recommendation created."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate insight",
        variant: "destructive"
      });
    }
    setGenerating(false);
  };

  // Barcode Scanner Functions
  const handleScanBarcode = async () => {
    if (!barcodeInput.trim()) {
      toast({
        title: "Error",
        description: "Please enter a barcode number",
        variant: "destructive"
      });
      return;
    }
    
    setScanning(true);
    try {
      const result = await foodAPI.getByBarcode(barcodeInput.trim());
      setScanResult(result);
      toast({
        title: "Product Found!",
        description: result.name
      });
    } catch (error) {
      toast({
        title: "Product Not Found",
        description: "Try a different barcode or search by name",
        variant: "destructive"
      });
      setScanResult(null);
    }
    setScanning(false);
  };

  // Food Search Functions
  const handleFoodSearch = async () => {
    if (!foodSearch.trim()) return;
    
    setSearchingFood(true);
    try {
      const results = await foodAPI.search(foodSearch.trim(), 8);
      setFoodResults(results.products || []);
      if (results.products?.length === 0) {
        toast({
          title: "No Results",
          description: "Try a different search term"
        });
      }
    } catch (error) {
      toast({
        title: "Search Failed",
        description: "Please try again",
        variant: "destructive"
      });
    }
    setSearchingFood(false);
  };

  // AI Chat Functions
  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await aiAPI.chat(userMessage, sessionId);
      setChatMessages(prev => [...prev, { role: 'assistant', text: response.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later." 
      }]);
    }
    setChatLoading(false);
  };

  const handleSyncDevice = () => {
    toast({
      title: "Syncing Device",
      description: "Connecting to your wearable device..."
    });
    
    setTimeout(() => {
      setDeviceData(prev => ({
        ...prev,
        last_sync: new Date().toISOString(),
        steps_today: prev.steps_today + Math.round(Math.random() * 500)
      }));
      toast({
        title: "Sync Complete!",
        description: "Latest data has been imported"
      });
    }, 2000);
  };

  const handleConnectDevice = (deviceName) => {
    toast({
      title: `Connecting to ${deviceName}`,
      description: "This feature will be available soon!"
    });
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800';
      case 'low':
        return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800';
      default:
        return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Brain className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Smart Features</h2>
              <p className="text-violet-100 text-lg">AI predictions, food scanner & health assistant</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setChatOpen(true)}
              className="bg-white/20 hover:bg-white/30"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              AI Chat
            </Button>
            <Button 
              variant="ghost" 
              onClick={fetchData}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* AI Chat Dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-lg dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <Brain className="w-5 h-5 text-violet-500" />
              DiaWise AI Assistant
            </DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Ask me anything about diabetes management!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="h-64 overflow-y-auto space-y-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {chatMessages.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                  Start a conversation! Ask about meal planning, glucose management, or any diabetes questions.
                </p>
              ) : (
                chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
                      msg.role === 'user' 
                        ? 'bg-violet-500 text-white' 
                        : 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Type your question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <Button onClick={handleSendChat} disabled={chatLoading}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="predictions" className="space-y-6">
        <TabsList className="grid w-full max-w-lg grid-cols-4">
          <TabsTrigger value="predictions">AI Insights</TabsTrigger>
          <TabsTrigger value="scanner">Food Scanner</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
        </TabsList>

        {/* AI Predictions Tab */}
        <TabsContent value="predictions" className="space-y-6">
          <Card className="shadow-md border-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 dark:bg-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <div>
                    <CardTitle className="dark:text-white">AI-Powered Health Insights</CardTitle>
                    <CardDescription className="dark:text-gray-400">
                      Personalized recommendations using GPT-4o AI
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  onClick={handleGeneratePrediction}
                  className="bg-gradient-to-r from-violet-500 to-purple-500"
                  disabled={generating}
                >
                  {generating ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Brain className="w-4 h-4 mr-2" /> Generate New</>
                  )}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {predictions.length === 0 ? (
            <Card className="shadow-md border-0 dark:bg-gray-800">
              <CardContent className="py-12 text-center">
                <Brain className="w-16 h-16 mx-auto mb-4 text-gray-400 opacity-50" />
                <p className="text-gray-500 dark:text-gray-400">No insights yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Log glucose data and click "Generate New" for AI insights</p>
                <Button onClick={handleGeneratePrediction} className="mt-4 bg-gradient-to-r from-violet-500 to-purple-500">
                  <Brain className="w-4 h-4 mr-2" />
                  Generate Your First Insight
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {predictions.map((insight, index) => (
                <Card key={insight.id || index} className={`shadow-md border-2 ${getPriorityStyles(insight.priority)} hover:shadow-lg transition-all dark:bg-gray-800`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                          <Brain className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <Badge className="mb-1">{insight.category || 'Insight'}</Badge>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(insight.date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className={insight.priority === 'high' ? 'border-orange-500 text-orange-500' : insight.priority === 'low' ? 'border-green-500 text-green-500' : ''}>
                        {insight.priority} priority
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-300">{insight.generated_text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Food Scanner Tab */}
        <TabsContent value="scanner" className="space-y-6">
          <Card className="shadow-md border-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-white">
                <Scan className="w-5 h-5 text-orange-500" />
                Food Scanner & Search
              </CardTitle>
              <CardDescription className="dark:text-gray-400">
                Scan barcodes or search for nutrition information (powered by OpenFoodFacts)
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Barcode Scanner */}
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white">Barcode Scanner</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter barcode number (e.g., 5000159407236)"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScanBarcode()}
                  className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <Button onClick={handleScanBarcode} disabled={scanning} className="bg-orange-500 hover:bg-orange-600">
                  {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                </Button>
              </div>
              
              {scanResult && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                  <div className="flex items-start gap-4">
                    {scanResult.image && (
                      <img src={scanResult.image} alt={scanResult.name} className="w-20 h-20 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{scanResult.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{scanResult.brand}</p>
                      {scanResult.nutriscore && (
                        <Badge className="mt-1">Nutri-Score: {scanResult.nutriscore.toUpperCase()}</Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setScanResult(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/30 rounded">
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-400">{scanResult.nutrition.carbs}g</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Carbs/100g</p>
                    </div>
                    <div className="text-center p-2 bg-orange-50 dark:bg-orange-900/30 rounded">
                      <p className="text-lg font-bold text-orange-700 dark:text-orange-400">{scanResult.nutrition.sugar}g</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Sugar/100g</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 dark:bg-green-900/30 rounded">
                      <p className="text-lg font-bold text-green-700 dark:text-green-400">{scanResult.nutrition.calories}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Calories/100g</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Food Search */}
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white">Search Food Database</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Search for food (e.g., oatmeal, apple, yogurt)"
                  value={foodSearch}
                  onChange={(e) => setFoodSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFoodSearch()}
                  className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <Button onClick={handleFoodSearch} disabled={searchingFood}>
                  {searchingFood ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              
              {foodResults.length > 0 && (
                <div className="grid md:grid-cols-2 gap-3">
                  {foodResults.map((food, index) => (
                    <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center gap-3">
                      {food.image ? (
                        <img src={food.image} alt={food.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                          <Apple className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{food.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{food.brand}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-blue-600 dark:text-blue-400">{food.nutrition.carbs}g carbs</span>
                          <span className="text-xs text-orange-600 dark:text-orange-400">{food.nutrition.sugar}g sugar</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-md border-0 bg-blue-50 dark:bg-blue-900/20 dark:bg-gray-800">
            <CardContent className="py-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Tip:</strong> Food data comes from OpenFoodFacts, a free and open database with millions of products worldwide. 
                Nutritional values are per 100g unless otherwise noted.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          {deviceData && (
            <Card className="shadow-md border-2 border-emerald-200 dark:border-emerald-800 dark:bg-gray-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center">
                      <Watch className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl dark:text-white">{deviceData.device_name}</CardTitle>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Last synced: {new Date(deviceData.last_sync).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{deviceData.steps_today.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Steps Today</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400">{deviceData.heart_rate_avg}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Avg Heart Rate</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{deviceData.sleep_hours}h</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Sleep Last Night</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{deviceData.active_minutes}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Active Minutes</p>
                  </div>
                </div>
                <Button onClick={handleSyncDevice} className="w-full bg-gradient-to-r from-emerald-500 to-green-500">
                  <Zap className="w-4 h-4 mr-2" />
                  Sync Now
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="dark:text-white">Available Integrations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { name: 'Fitbit', icon: Watch, color: 'from-cyan-400 to-blue-500' },
                  { name: 'Apple Health', icon: Smartphone, color: 'from-gray-400 to-gray-600' },
                  { name: 'Google Fit', icon: Smartphone, color: 'from-green-400 to-emerald-500' },
                  { name: 'Dexcom CGM', icon: Activity, color: 'from-orange-400 to-red-500' },
                  { name: 'Freestyle Libre', icon: Activity, color: 'from-blue-400 to-indigo-500' },
                  { name: 'Garmin', icon: Watch, color: 'from-blue-500 to-cyan-600' }
                ].map((device, index) => {
                  const Icon = device.icon;
                  return (
                    <div key={index} className="p-4 border rounded-lg hover:shadow-md transition-all dark:border-gray-700">
                      <div className={`w-12 h-12 bg-gradient-to-br ${device.color} rounded-lg flex items-center justify-center mb-3`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white">{device.name}</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 w-full dark:border-gray-600"
                        onClick={() => handleConnectDevice(device.name)}
                      >
                        Connect
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-6">
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-white">
                <Zap className="w-5 h-5 text-yellow-500" />
                Smart Automation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-800 dark:text-green-400">AI Insights Active</h4>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300">
                  GPT-4o powered insights analyze your glucose and meal data to provide personalized recommendations.
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-800 dark:text-blue-400">Food Database Connected</h4>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  OpenFoodFacts integration provides nutrition info for millions of products worldwide.
                </p>
              </div>
              
              <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-purple-800 dark:text-purple-400">AI Health Assistant</h4>
                </div>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Chat with our AI assistant for instant answers to your diabetes questions.
                </p>
                <Button onClick={() => setChatOpen(true)} className="mt-2 bg-purple-500 hover:bg-purple-600">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Open AI Chat
                </Button>
              </div>
            </CardContent>
          </Card>

          {analytics && (
            <Card className="shadow-md border-0 dark:bg-gray-800">
              <CardHeader>
                <CardTitle className="dark:text-white">Your Data Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.total_glucose_logs}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Glucose Logs</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.total_meals}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Meals Logged</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{analytics.in_range_percentage}%</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">In Range</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analytics.average_glucose || '--'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Avg Glucose</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Apple icon component
const Apple = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C9.5 2 7.5 3.5 7 5.5C6.5 3.5 4.5 2 2 2C2 5.5 5 8 8 8C5.5 8 3 10.5 3 14C3 17.5 6 22 9 22C10.5 22 11.5 21 12 20C12.5 21 13.5 22 15 22C18 22 21 17.5 21 14C21 10.5 18.5 8 16 8C19 8 22 5.5 22 2C19.5 2 17.5 3.5 17 5.5C16.5 3.5 14.5 2 12 2Z"/>
  </svg>
);

export default SmartFeatures;
